import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

export interface ViewModalRow {
  label: string;
  value: React.ReactNode;
}

export interface ViewModalProps {
  open: boolean;
  onClose: () => void;
  /** Modal title (e.g. "Client details") */
  title: string;
  /** Optional subtitle under the title (e.g. type + status) */
  subtitle?: React.ReactNode;
  /** Optional: pass rows for a consistent label-value layout */
  rows?: ViewModalRow[];
  /** Or pass custom content as children (ignored if rows is set) */
  children?: React.ReactNode;
  /** Called when Edit is clicked; if not provided, Edit button is hidden */
  onEdit?: () => void;
  /** Edit button label */
  editLabel?: string;
  /** maxWidth */
  maxWidth?: "xs" | "sm" | "md" | "lg";
}

/**
 * Read-only view modal for clients, providers, locations, etc.
 * Use rows for a clean label-value list, or children for custom content.
 */
export default function ViewModal({
  open,
  onClose,
  title,
  subtitle,
  rows,
  children,
  onEdit,
  editLabel = "Edit",
  maxWidth = "sm",
}: ViewModalProps) {
  const content = rows ? (
    <Stack spacing={0} sx={{ pt: 0.5 }}>
      {rows.map(({ label, value }, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            py: 1.25,
            borderBottom: i < rows.length - 1 ? 1 : 0,
            borderColor: "divider",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="body2" component="span">
            {value ?? "—"}
          </Typography>
        </Box>
      ))}
    </Stack>
  ) : (
    children
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ pt: subtitle ? 1 : 2, pb: 2 }}>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}
        {content}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
        {onEdit && (
          <Button
            variant="contained"
            onClick={onEdit}
            startIcon={<EditOutlinedIcon fontSize="small" />}
          >
            {editLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
